import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { DateLike } from '../../util/dateTime'

// mergeDateTime parses typed times ("7:00pm") with a format string, which needs this plugin
// wherever the class is used, not only where the app entrypoint has loaded it.
dayjs.extend(customParseFormat)

class EventStartEndDateTime {
  public startAt: Date
  public endAt: Date
  // A flyer that shows a date but no time leaves the new-event form's time boxes blank
  // (CVL-173) instead of showing a time nobody gave. Picking a time clears the flag.
  public startAtTimeBlank?: boolean
  public endAtTimeBlank?: boolean

  public constructor({ startAt, endAt }: { startAt: Date; endAt: Date }) {
    this.startAt = startAt
    this.endAt = endAt
  }

  private mergeDateTime(date?: DateLike, time?: string): Date {
    const dateTime = dayjs(date)
    if (!time) {
      return dateTime.toDate()
    }

    // dayjs won't work with time only, so we'll add an arbitrary date to the time string so it can be parsed
    const timeMoment = dayjs('12-17-1986 ' + time, 'MM-DD-YYYY h:mma')

    if (!timeMoment.isValid()) {
      return dateTime.toDate()
    }

    return dateTime.set('hour', timeMoment.get('hour')).set('minute', timeMoment.get('minute')).startOf('minute').toDate()
  }

  private splitDateTime(dateTime?: DateLike): { date?: Date; time: string } {
    const timeMoment = dayjs(dateTime)
    const time = timeMoment.format('h:mma')
    return {
      date: timeMoment.toDate(),
      time,
    }
  }

  public get startAtDate(): Date | undefined {
    return this.startAt
  }

  public set startAtDate(value: Date | undefined) {
    this.startAt = this.mergeDateTime(value, this.startAtTime)
    this.endAt = this.mergeDateTime(value, this.endAtTime)
  }

  public get startAtTime(): string {
    if (this.startAtTimeBlank) return ''
    return this.splitDateTime(this.startAt).time
  }

  public set startAtTime(value: string) {
    this.startAt = this.mergeDateTime(this.startAtDate, value)
    if (this.startAtTimeBlank) this.startAtTimeBlank = !value
  }

  public get endAtDate(): Date | undefined {
    return this.endAt
  }

  public set endAtDate(value: Date | undefined) {
    this.endAt = this.mergeDateTime(value, this.endAtTime)
  }

  public get endAtTime(): string {
    if (this.endAtTimeBlank) return ''
    return this.splitDateTime(this.endAt).time
  }

  public set endAtTime(value: string) {
    this.endAt = this.mergeDateTime(this.endAtDate, value)
    if (this.endAtTimeBlank) this.endAtTimeBlank = !value
  }
}

export default EventStartEndDateTime
